import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CloudOff, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SyncIndicatorProps {
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
}

export function SyncIndicator({ pendingCount, failedCount, isSyncing }: SyncIndicatorProps) {
  const [showComplete, setShowComplete] = useState(false);
  const prevSyncingRef = useRef(false);

  useEffect(() => {
    const wasSyncing = prevSyncingRef.current;
    prevSyncingRef.current = isSyncing;

    if (!wasSyncing || isSyncing || pendingCount !== 0 || failedCount !== 0) return;

    // Atualizar estado via timer para evitar setState síncrono no corpo do efeito
    const showTimer = setTimeout(() => setShowComplete(true), 0);
    const hideTimer = setTimeout(() => setShowComplete(false), 2000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [isSyncing, pendingCount, failedCount]);

  // Prioridade: failed > syncing > pending > complete > null
  if (failedCount > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5" aria-label={`${failedCount} ações falharam`}>
            <AlertTriangle size={18} className="text-amber-600" />
            <Badge variant="secondary" className="bg-red-100 px-1.5 py-0 text-[0.7rem] text-red-700">
              {failedCount}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {failedCount} {failedCount === 1 ? 'ação falhou' : 'ações falharam'}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (showComplete) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center" aria-label="Sincronização concluída">
            <Check
              size={18}
              className="animate-in fade-in-0 zoom-in-50 text-green-600"
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Sincronização concluída</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isSyncing) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center" aria-label="Sincronizando">
            <RefreshCw size={18} className="animate-spin text-[var(--color-primary)]" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Sincronizando...</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (pendingCount === 0) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1.5" aria-label={`${pendingCount} ações pendentes`}>
          <CloudOff size={16} className="text-[var(--color-text-secondary)]" />
          <Badge variant="secondary" className="px-1.5 py-0 text-[0.7rem]">
            {pendingCount}
          </Badge>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          {pendingCount} {pendingCount === 1 ? 'ação pendente será sincronizada' : 'ações pendentes serão sincronizadas'} ao reconectar
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
